/**
 * F-296 / T-232-002 — Lead scoring dashboard. Renders the 0-100 scores +
 * Top-20 lead list surfaced from the canonical /api/lead-scoring pipeline
 * (admin + tenant-scoped). Until the engine has data this page renders its
 * "warming up" state instead of erroring.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { leadScoringApi, LeadScoringSnapshot, bandOf, clampScore } from '../../services/leadScoring';

const TOP_N = 20;

export const LeadScoringDashboard: React.FC = () => {
    const [snapshot, setSnapshot] = useState<LeadScoringSnapshot | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setSnapshot(await leadScoringApi.getSummary());
        } catch (e: any) {
            setError(e?.message || 'Unable to load lead scores');
            setSnapshot(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const top = snapshot?.leads
        ? [...snapshot.leads].sort((a, b) => clampScore(b.score) - clampScore(a.score)).slice(0, TOP_N)
        : [];
    const maxBand = snapshot?.bands.length ? Math.max(...snapshot.bands.map((b) => b.count), 1) : 1;

    const bandColor = (score: number): string => {
        const band = bandOf(score);
        if (band === 'Hot') return 'bg-green-500';
        if (band === 'Warm') return 'bg-amber-500';
        return 'bg-gray-300';
    };

    const bandBadgeClass = (score: number): string => {
        const band = bandOf(score);
        if (band === 'Hot') return 'bg-green-100 text-green-700';
        if (band === 'Warm') return 'bg-amber-100 text-amber-700';
        return 'bg-gray-100 text-gray-600';
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="lead-scoring-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-1">Lead Scoring</h1>
                    <p className="text-sm text-gray-500">
                        Growth-engine scores (0-100) for organic + inbound leads, surfaced from the scoring pipeline.
                    </p>
                </div>
                <button onClick={load} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm">
                    Refresh
                </button>
            </div>

            {loading && <p className="text-gray-500" data-testid="ls-loading">Loading lead scores…</p>}

            {error && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-6" data-testid="ls-error">
                    <p className="font-semibold mb-1">Lead scoring is not live yet.</p>
                    <p className="text-sm">
                        {error} The scoring engine is part of the F-296 growth pipeline and is being activated.
                        Scores appear here automatically once the engine starts scoring.
                    </p>
                </div>
            )}

            {!loading && !error && (!snapshot || snapshot.total === 0) && (
                <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center text-gray-500" data-testid="ls-empty">
                    No leads scored yet. Scores appear once the scoring engine sees lead activity.
                </div>
            )}

            {!loading && !error && snapshot && snapshot.total > 0 && (
                <>
                    <div className="grid gap-4 sm:grid-cols-3 mb-6">
                        <div className="bg-white rounded-xl shadow p-5" data-testid="ls-total">
                            <p className="text-sm text-gray-500">Scored leads</p>
                            <p className="text-2xl font-bold text-gray-900">{snapshot.total}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow p-5" data-testid="ls-average">
                            <p className="text-sm text-gray-500">Average score</p>
                            <p className="text-2xl font-bold text-gray-900">{snapshot.averageScore.toFixed(1)}</p>
                        </div>
                        <div className="bg-white rounded-xl shadow p-5">
                            <p className="text-sm text-gray-500 mb-2">Band distribution</p>
                            <div className="flex h-3 bg-gray-100 rounded overflow-hidden mb-2" data-testid="ls-band-bar">
                                {snapshot.bands.map((b) => (
                                    <div
                                        key={b.label}
                                        title={`${b.label} ${b.count}`}
                                        className={b.label === 'Hot' ? 'bg-green-500' : b.label === 'Warm' ? 'bg-amber-500' : 'bg-gray-300'}
                                        style={{ width: `${(b.count / Math.max(snapshot.total, 1)) * 100}%` }}
                                    />
                                ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500">
                                {snapshot.bands.map((b) => (
                                    <span key={b.label} data-testid={`ls-band-count-${b.label.toLowerCase()}`}>
                                        {b.label} {b.count} ({Math.round((b.count / Math.max(snapshot.total, 1)) * 100)}%)
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow overflow-hidden">
                        <h2 className="text-lg font-semibold px-6 pt-5 pb-1">Top scored leads</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="ls-top-leads">
                                <thead>
                                    <tr className="text-left text-gray-500 border-b">
                                        <th className="py-2 px-6">#</th>
                                        <th className="py-2 px-3">Lead</th>
                                        <th className="py-2 px-3">Segment</th>
                                        <th className="py-2 px-3">Jurisdiction</th>
                                        <th className="py-2 px-6 w-40">Score (0-100)</th>
                                        <th className="py-2 px-3">Band</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {top.map((lead, idx) => (
                                        <tr key={lead.id} className="border-b last:border-0" data-testid="ls-top-lead-row">
                                            <td className="py-2 px-6 text-gray-400">{idx + 1}</td>
                                            <td className="py-2 px-3">
                                                <span className="font-medium text-gray-800">{lead.name || lead.email || lead.company || lead.id}</span>
                                                {lead.email && lead.email !== lead.name && (
                                                    <span className="block text-xs text-gray-400">{lead.email}</span>
                                                )}
                                            </td>
                                            <td className="py-2 px-3">{lead.segment || '—'}</td>
                                            <td className="py-2 px-3">
                                                {lead.jurisdiction ||
                                                    (lead.location && [lead.location.city, lead.location.state].filter(Boolean).join(', ')) ||
                                                    '—'}
                                            </td>
                                            <td className="py-2 px-6">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-2 bg-gray-100 rounded flex-1 overflow-hidden">
                                                        <div
                                                            className={`h-2 ${bandColor(lead.score)} rounded`}
                                                            style={{ width: `${clampScore(lead.score)}%` }}
                                                        />
                                                    </div>
                                                    <span className="font-semibold" data-testid="ls-score">{clampScore(lead.score)}</span>
                                                </div>
                                            </td>
                                            <td className="py-2 px-3">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${bandBadgeClass(lead.score)}`} data-testid="ls-band">
                                                    {bandOf(lead.score)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LeadScoringDashboard;